import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { StarRating } from "@/components/StarRating";
import { X } from "lucide-react";

const CRITERIA = [
    { key: "punctuality", label: "Ponctualité" },
    { key: "professionalism", label: "Professionnalisme" },
    { key: "appearance", label: "Tenue & présentation" },
    { key: "attitude", label: "Savoir-être & relation client" },
];

/**
 * Modal to submit a post-mission evaluation (4 star ratings + would_recommend + comment).
 * Props: open (bool), pending (obj|null), onClose(fn), onSubmitted(fn).
 */
export default function EvaluationModal({ open, pending, onClose, onSubmitted }) {
    const [ratings, setRatings] = useState({ punctuality: 0, professionalism: 0, appearance: 0, attitude: 0 });
    const [wouldRecommend, setWouldRecommend] = useState(true);
    const [comment, setComment] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        if (open) {
            setRatings({ punctuality: 0, professionalism: 0, appearance: 0, attitude: 0 });
            setWouldRecommend(true);
            setComment("");
            setError("");
        }
    }, [open, pending?.application_id]);

    if (!open || !pending) return null;

    const allSet = CRITERIA.every((c) => ratings[c.key] > 0);

    const submit = async () => {
        if (!allSet) { setError("Merci de noter les 4 critères."); return; }
        setSubmitting(true);
        setError("");
        try {
            await api.post("/evaluations", {
                application_id: pending.application_id,
                punctuality: ratings.punctuality,
                professionalism: ratings.professionalism,
                appearance: ratings.appearance,
                attitude: ratings.attitude,
                would_recommend: wouldRecommend,
                comment,
            });
            if (onSubmitted) onSubmitted();
            onClose();
        } catch (e) {
            setError(e?.response?.data?.detail || "Erreur lors de l'envoi.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div
            className="fixed inset-0 z-50 bg-[#0B192C]/70 backdrop-blur-sm flex items-center justify-center p-4"
            data-testid="evaluation-modal"
            onClick={onClose}
        >
            <div
                className="bg-white rounded-2xl max-w-lg w-full shadow-2xl overflow-hidden"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex justify-between items-start p-6 border-b border-slate-100">
                    <div>
                        <div className="overline text-[#F5A623]">Évaluation post-mission</div>
                        <h2 className="font-display text-xl mt-1">
                            {pending.candidate?.first_name} {pending.candidate?.last_name}
                        </h2>
                        <div className="text-xs text-slate-500 mt-1">
                            {pending.mission_title} · {pending.mission_date} · {pending.mission_city}
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-slate-400 hover:text-slate-600"
                        data-testid="evaluation-close"
                    >
                        <X size={20} />
                    </button>
                </div>

                <div className="p-6 space-y-5 max-h-[70vh] overflow-y-auto">
                    {CRITERIA.map((c) => (
                        <div key={c.key} className="flex justify-between items-center gap-3 flex-wrap">
                            <div className="text-sm font-semibold text-[#0B192C]">{c.label}</div>
                            <StarRating
                                value={ratings[c.key]}
                                onChange={(n) => setRatings((r) => ({ ...r, [c.key]: n }))}
                                testIdPrefix={`eval-${c.key}`}
                            />
                        </div>
                    ))}

                    <div className="pt-2 border-t border-slate-100">
                        <div className="text-sm font-semibold mb-2">
                            Recommanderiez-vous ce prestataire ?
                        </div>
                        <div className="flex gap-2">
                            <button
                                type="button"
                                onClick={() => setWouldRecommend(true)}
                                className={`px-4 py-2 rounded-full text-sm font-semibold border-2 transition ${
                                    wouldRecommend
                                        ? "border-[#F5A623] bg-[#F5A623]/10 text-[#0B192C]"
                                        : "border-slate-200 text-slate-500"
                                }`}
                                data-testid="eval-recommend-yes"
                            >
                                Oui, sans hésiter
                            </button>
                            <button
                                type="button"
                                onClick={() => setWouldRecommend(false)}
                                className={`px-4 py-2 rounded-full text-sm font-semibold border-2 transition ${
                                    !wouldRecommend
                                        ? "border-red-400 bg-red-50 text-red-700"
                                        : "border-slate-200 text-slate-500"
                                }`}
                                data-testid="eval-recommend-no"
                            >
                                Non
                            </button>
                        </div>
                    </div>

                    <div>
                        <label className="text-sm font-semibold block mb-2">
                            Commentaire <span className="text-xs text-slate-400 font-normal">(facultatif, visible uniquement par Renforteo)</span>
                        </label>
                        <textarea
                            value={comment}
                            onChange={(e) => setComment(e.target.value)}
                            rows={3}
                            maxLength={2000}
                            className="w-full border border-slate-200 rounded-lg p-3 text-sm focus:border-[#F5A623] focus:ring-1 focus:ring-[#F5A623]/40 outline-none"
                            placeholder="Un incident, un point remarquable à signaler ?"
                            data-testid="eval-comment"
                        />
                    </div>

                    {error && (
                        <div className="text-sm text-red-600" data-testid="eval-error">{error}</div>
                    )}
                </div>

                <div className="p-4 border-t border-slate-100 flex justify-end gap-2 bg-slate-50">
                    <button
                        onClick={onClose}
                        className="btn-ghost-navy text-sm"
                        data-testid="eval-cancel"
                    >
                        Annuler
                    </button>
                    <button
                        onClick={submit}
                        disabled={!allSet || submitting}
                        className="btn-gold text-sm disabled:opacity-50"
                        data-testid="eval-submit"
                    >
                        {submitting ? "Envoi…" : "Envoyer l'évaluation"}
                    </button>
                </div>
            </div>
        </div>
    );
}
